import { css } from '@emotion/css';
import { useEffect, useMemo, useRef, useState } from 'react';

import { type GrafanaTheme2 } from '@grafana/data';
import { t } from '@grafana/i18n';
import { EmptyState, Pagination, useStyles2 } from '@grafana/ui';
import { useQueryLibraryContext } from 'app/features/explore/QueryLibrary/QueryLibraryContext';
import { type SavedQuery } from 'app/features/explore/QueryLibrary/types';

import { selectors } from '../e2e-selectors/selectors';

import { SavedQueriesDetails } from './SavedQueriesDetails';
import { SavedQueriesList } from './SavedQueriesList';

type Props = {
  queryRows: SavedQuery[];
  newQuery: SavedQuery | undefined;
  isLoading: boolean;
};

const PAGE_SIZE = 25;

// Manages selection state and renders the split view: scrollable query list on the left, query details on the right.
export function SavedQueriesLibrary({ queryRows, newQuery, isLoading }: Props) {
  const styles = useStyles2(getStyles);
  const [selectedUid, setSelectedUid] = useState<string | undefined>();
  const [liveTitle, setLiveTitle] = useState<string | undefined>();
  const [currentPage, setCurrentPage] = useState(1);
  const { highlightedQuery, isEditingQuery } = useQueryLibraryContext();
  const listRef = useRef<HTMLDivElement>(null);
  const previousHighlightedQueryRef = useRef(highlightedQuery);
  // Tracks a uid that was just created and hasn't appeared in queryRows yet.
  // Prevents the auto-select-first effect from overriding the selection before the list refetches.
  const pendingNewUidRef = useRef<string | undefined>(undefined);
  const numberOfPages = Math.ceil(queryRows.length / PAGE_SIZE);
  const visiblePage = Math.min(currentPage, Math.max(numberOfPages, 1));
  const queryRowsIdentity = JSON.stringify(queryRows.map((query) => query.uid));
  const previousQueryRowsIdentityRef = useRef(queryRowsIdentity);
  const highlightedQueryIndex = highlightedQuery ? queryRows.findIndex((query) => query.uid === highlightedQuery) : -1;
  const visibleQueryRows = useMemo(() => {
    const start = (visiblePage - 1) * PAGE_SIZE;
    return queryRows.slice(start, start + PAGE_SIZE);
  }, [queryRows, visiblePage]);

  useEffect(() => {
    const queryRowsChanged = previousQueryRowsIdentityRef.current !== queryRowsIdentity;
    previousQueryRowsIdentityRef.current = queryRowsIdentity;
    if (isEditingQuery) {
      previousHighlightedQueryRef.current = highlightedQuery;
      return;
    }
    if (highlightedQuery) {
      // TODO: move this page/selection syncing out of effects
      // eslint-disable-next-line react-hooks/set-state-in-effect
      setCurrentPage(highlightedQueryIndex >= 0 ? Math.floor(highlightedQueryIndex / PAGE_SIZE) + 1 : 1);
    } else if (queryRowsChanged && !previousHighlightedQueryRef.current) {
      // Reset pagination when the filtered result set changes, but preserve the page when
      // query metadata changes, editing ends, or the short-lived highlight expires.
      setCurrentPage(1);
    }
    previousHighlightedQueryRef.current = highlightedQuery;
  }, [highlightedQuery, highlightedQueryIndex, isEditingQuery, queryRowsIdentity]);

  // Auto-select and scroll to highlighted query when it arrives in the list
  useEffect(() => {
    if (!highlightedQuery) {
      return;
    }
    const inList = queryRows.some((q) => q.uid === highlightedQuery);
    if (!inList) {
      return;
    }
    // TODO: move this page/selection syncing out of effects
    // eslint-disable-next-line react-hooks/set-state-in-effect
    setSelectedUid(highlightedQuery);
    const timer = window.setTimeout(() => {
      const el = listRef.current?.querySelector(`[data-query-uid="${highlightedQuery}"]`);
      el?.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
    }, 0);
    return () => window.clearTimeout(timer);
  }, [highlightedQuery, queryRows]);

  // Auto-select the first query when loading finishes with no selection,
  // or when a filter change causes the selected query to leave the visible list.
  // Yields to the highlightedQuery effect when a specific query should be shown.
  useEffect(() => {
    if (isLoading || isEditingQuery || visibleQueryRows.length === 0 || newQuery) {
      return;
    }
    // The highlightedQuery effect handles selection when a specific query is targeted;
    // both effects run in the same pass so we must check highlightedQuery directly
    // (the state update from the sibling effect hasn't flushed yet).
    if (highlightedQuery && queryRows.some((q) => q.uid === highlightedQuery)) {
      return;
    }
    // A newly created query may not have arrived in queryRows yet (list still refetching).
    // Don't override the pending selection — wait for it to appear.
    if (pendingNewUidRef.current) {
      return;
    }
    const selectedStillVisible = visibleQueryRows.some((q) => q.uid === selectedUid);
    if (!selectedUid || !selectedStillVisible) {
      // TODO: move this page/selection syncing out of effects
      // eslint-disable-next-line react-hooks/set-state-in-effect
      setSelectedUid(visibleQueryRows[0].uid);
    }
  }, [isLoading, isEditingQuery, queryRows, visibleQueryRows, selectedUid, newQuery, highlightedQuery]);

  // Open the page containing a newly created query once it appears in the list.
  useEffect(() => {
    if (pendingNewUidRef.current) {
      const newQueryIndex = queryRows.findIndex((query) => query.uid === pendingNewUidRef.current);
      if (newQueryIndex >= 0) {
        setSelectedUid(pendingNewUidRef.current);
        setCurrentPage(Math.floor(newQueryIndex / PAGE_SIZE) + 1);
        pendingNewUidRef.current = undefined;
      }
    }
  }, [queryRows]);

  // Scroll to top when a new pending query arrives (it's always rendered first)
  useEffect(() => {
    if (newQuery) {
      // TODO: move this page/selection syncing out of effects
      // eslint-disable-next-line react-hooks/set-state-in-effect
      setCurrentPage(1);
      listRef.current?.parentElement?.scrollTo({ top: 0, behavior: 'smooth' });
    }
  }, [newQuery]);

  const selectedQuery = useMemo(() => {
    // newQuery always takes priority — it's a pending save that needs immediate attention
    if (newQuery) {
      return newQuery;
    }
    if (!selectedUid) {
      return undefined;
    }
    return queryRows.find((q) => q.uid === selectedUid);
  }, [selectedUid, queryRows, newQuery]);

  if (!isLoading && !newQuery && queryRows.length === 0) {
    return (
      <div
        className={styles.library}
        role="region"
        aria-label={t('saved-queries.library.region-label', 'Query library')}
      >
        <div className={styles.emptyStateContainer}>
          <EmptyState variant="not-found" message={t('saved-queries.list.empty', 'No saved queries found')} />
        </div>
      </div>
    );
  }

  return (
    <div
      className={styles.library}
      role="region"
      aria-label={t('saved-queries.library.region-label', 'Query library')}
      data-testid={selectors.components.savedQueriesModal.content}
    >
      <div className={styles.half}>
        <SavedQueriesList
          queryRows={visibleQueryRows}
          newQuery={newQuery}
          isLoading={isLoading}
          selectedUid={selectedUid}
          liveTitle={liveTitle}
          listRef={listRef}
          onSelect={(query) => {
            pendingNewUidRef.current = undefined;
            setSelectedUid(query.uid);
          }}
        />
        {numberOfPages > 1 && (
          <fieldset className={styles.paginationFieldset} disabled={isEditingQuery}>
            <div className={styles.pagination}>
              <Pagination
                currentPage={visiblePage}
                numberOfPages={numberOfPages}
                onNavigate={(toPage) => {
                  const firstQuery = queryRows[(toPage - 1) * PAGE_SIZE];
                  setCurrentPage(toPage);
                  setSelectedUid(firstQuery?.uid);
                  listRef.current?.parentElement?.scrollTo({ top: 0 });
                }}
              />
            </div>
          </fieldset>
        )}
      </div>
      <div className={styles.half}>
        <SavedQueriesDetails
          selectedQuery={selectedQuery}
          onTitleChange={setLiveTitle}
          onDelete={() => setSelectedUid(undefined)}
          onSaveNew={(uid) => {
            const savedQueryIndex = queryRows.findIndex((query) => query.uid === uid);
            pendingNewUidRef.current = savedQueryIndex >= 0 ? undefined : uid;
            if (savedQueryIndex >= 0) {
              setCurrentPage(Math.floor(savedQueryIndex / PAGE_SIZE) + 1);
            }
            setSelectedUid(uid);
          }}
        />
      </div>
    </div>
  );
}

const getStyles = (theme: GrafanaTheme2) => ({
  library: css({
    display: 'flex',
    flexDirection: 'row',
    flex: 1,
    minWidth: 0,
    overflow: 'hidden',
    [theme.breakpoints.down('lg')]: {
      flexDirection: 'column',
    },
  }),
  emptyStateContainer: css({
    flex: 1,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  }),
  half: css({
    flex: '1 1 0%',
    minWidth: 0,
    overflow: 'hidden',
    height: '100%',
    display: 'flex',
    flexDirection: 'column',
    [theme.breakpoints.down('lg')]: {
      flex: 'none',
      height: '50%',
    },
  }),
  pagination: css({
    display: 'flex',
    justifyContent: 'center',
    padding: theme.spacing(1.5, 2, 0.5),
  }),
  paginationFieldset: css({
    border: 0,
    margin: 0,
    minWidth: 0,
    padding: 0,
  }),
});
